import type { TemplatedJson } from "@productlane/lib"
import { misplacedTypedTokens, StructuredValue } from "@productlane/lib"

import type { JsonKind } from "./tool-schema-fields"
import { jsonKindOf } from "./typed-value"

export type JsonDraft =
	| { ok: true; value: TemplatedJson; warnings: string[] }
	| { ok: false; error: string }

export interface JsonDraftOptions {
	/** The container types the argument accepts. Empty accepts either. */
	kinds: JsonKind[]
	/** Property names the schema declares, for an unknown-key warning. */
	knownKeys?: readonly string[]
}

function describeIssue(message: string, path: (string | number)[]): string {
	if (path.length === 0) return message
	const where = path
		.map((part) => (typeof part === "number" ? `[${part}]` : part))
		.join(".")
		.replace(/\.\[/g, "[")
	return `${where}: ${message}`
}

function unknownKeys(
	value: TemplatedJson,
	knownKeys: readonly string[],
): string[] {
	if (Array.isArray(value) || typeof value !== "object" || value === null) {
		return []
	}
	return Object.keys(value).filter((key) => !knownKeys.includes(key))
}

/**
 * Validates against the same Zod schema the mutator re-checks, so the editor and
 * the server can never disagree about what saves.
 */
export function parseJsonDraft(
	text: string,
	options: JsonDraftOptions,
): JsonDraft {
	if (!text.trim()) return { ok: false, error: "Enter a JSON object or list." }

	let raw: unknown
	try {
		raw = JSON.parse(text)
	} catch (error) {
		return {
			ok: false,
			error: error instanceof Error ? error.message : "This is not valid JSON.",
		}
	}

	const parsed = StructuredValue.safeParse(raw)
	if (!parsed.success) {
		const issue = parsed.error.issues[0]!
		return { ok: false, error: describeIssue(issue.message, issue.path) }
	}

	const value = parsed.data as TemplatedJson
	// The write path refuses this too, but reporting it here puts the message on
	// the leaf the author is typing rather than on the save.
	const [misplaced] = misplacedTypedTokens({ mode: "json", value })
	if (misplaced) {
		return {
			ok: false,
			error: `{{${misplaced}}} sets the value's type, so it has to be the whole leaf rather than sit inside text.`,
		}
	}

	const kind = jsonKindOf(value)
	const wanted: JsonKind[] = options.kinds.filter(
		(entry) => entry === "object" || entry === "array",
	)
	if (wanted.length > 0 && !wanted.includes(kind)) {
		return {
			ok: false,
			error: `This argument takes ${wanted.join(" or ")}, not ${kind}.`,
		}
	}

	// Warnings, not errors: additionalProperties is common and the flattened field
	// list stops at a fixed depth.
	const warnings = options.knownKeys
		? unknownKeys(value, options.knownKeys).map(
				(key) => `"${key}" is not an argument this tool documents.`,
			)
		: []

	return { ok: true, value, warnings }
}

export function formatJsonDraft(text: string): string {
	try {
		return JSON.stringify(JSON.parse(text), null, 2)
	} catch {
		return text
	}
}

export function toJsonDraftText(value: TemplatedJson): string {
	return JSON.stringify(value, null, 2)
}
